import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';

import Footer from './components/Footer';
import GoogleAnalytics from './components/GoogleAnalytics';
import { useAccessibility, useKeyboardNavigation, useAriaLiveRegion } from './hooks/useAccessibility';
import { useTheme } from './hooks/useTheme';
import './i18n'; // Initialize i18n

// Eager load Home page for fast initial load
import Home from './pages/Home';

// Lazy load other pages to reduce initial bundle size
const About = lazy(() => import('./pages/About'));
const Programs = lazy(() => import('./pages/Programs'));
const Impact = lazy(() => import('./pages/Impact'));
const Partners = lazy(() => import('./pages/Partners'));
const Team = lazy(() => import('./pages/Team'));
const Funders = lazy(() => import('./pages/Funders'));
const Stories = lazy(() => import('./pages/Stories'));
const StoryDetail = lazy(() => import('./pages/StoryDetail'));
const Volunteer = lazy(() => import('./pages/Volunteer'));
const Contact = lazy(() => import('./pages/Contact'));
const ComingSoon = lazy(() => import('./pages/ComingSoon'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const { preferences, announceToScreenReader } = useAccessibility();
  const { isDark } = useTheme();
  
  // Initialize accessibility features
  useKeyboardNavigation();
  const { announce } = useAriaLiveRegion();

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
            announceToScreenReader('Open Hope Initiative is now available offline');
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }

    // Add keyboard navigation class for focus indicators
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        document.body.classList.add('keyboard-user');
      }
    };

    const handleMouseDown = () => {
      document.body.classList.remove('keyboard-user');
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [announceToScreenReader]);

  return (
    <HelmetProvider>
      <GoogleAnalytics />
      <Router>
        <ScrollToTop />
        {/* Skip to main content link for accessibility */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        
        <div 
          className="min-h-screen flex flex-col transition-colors duration-300"
          style={{ 
            backgroundColor: 'var(--color-background)',
            color: 'var(--color-text-primary)'
          }}
        >

          <Navbar />
          <main 
            id="main-content" 
            className="flex-grow"
            tabIndex={-1}
            role="main"
            aria-label="Main content"
          >
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/programs" element={<Programs />} />
                <Route path="/impact" element={<Impact />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/team" element={<Team />} />
                <Route path="/funders" element={<Funders />} />
                <Route path="/stories" element={<Stories />} />
                <Route path="/stories/:slug" element={<StoryDetail />} />
                <Route path="/volunteer" element={<Volunteer />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/technologies" element={<ComingSoon />} />
                {/* Redirect removed auth routes to home */}
                <Route path="/signin" element={<Navigate to="/" replace />} />
                <Route path="/signup" element={<Navigate to="/" replace />} />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/register" element={<Navigate to="/" replace />} />
                {/* Placeholder routes for utility pages */}
                <Route path="/privacy" element={<div className="section-spacing"><div className="container-wrapper"><h1>Privacy Policy</h1><p>Coming soon...</p></div></div>} />
                <Route path="/terms" element={<div className="section-spacing"><div className="container-wrapper"><h1>Terms of Service</h1><p>Coming soon...</p></div></div>} />
                <Route path="/safeguarding" element={<div className="section-spacing"><div className="container-wrapper"><h1>Safeguarding</h1><p>Coming soon...</p></div></div>} />
                <Route path="/press-kit" element={<div className="section-spacing"><div className="container-wrapper"><h1>Press Kit</h1><p>Coming soon...</p></div></div>} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
          <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: preferences.highContrast ? '#ffffff' : '#f3f4f6',
                color: preferences.highContrast ? '#000000' : '#1f2937',
                border: preferences.highContrast ? '2px solid #000000' : 'none',
              },
            }}
          />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;